import React, { useCallback } from 'react';
import classnames from 'clsx';

import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { Text, TextType } from 'ui-components/components/text';

import { useEnableLotTracking } from '../../../../../../feature-flags/hooks/use-enable-lot-tracking';
import { useUtcFormattedDate } from '../../../../../../hooks/use-datetime-formats';
import { useModal } from '../../../../../../hooks/use-modal';
import { messages } from '../messages';
import { InventoryHistoryModal } from '../modals/history';
import { AddInventoryPopover } from '../pop-overs/add-inventory';
import { EditLotPopover } from '../pop-overs/edit-lot';
import { SetInventoryPopover } from '../pop-overs/set-product-inventory';
import { SubtractInventoryPopover } from '../pop-overs/subtract-inventory';
import style from '../style.module.css';
import { LocationActionsMenu } from './location-actions-menu';

/**
 * Component for displaying inventory location details and actions
 * @param {object} props
 * @param {string} props.productId
 * @param {string} [props.warehouseName]
 * @param {string} [props.location]
 * @param {string} [props.lot]
 * @param {string} [props.lotUuid]
 * @param {string} [props.expirationDate]
 * @param {number} [props.stock]
 * @param {number} [props.allocated]
 * @param {number} [props.available]
 * @param {(inventoryLocationId: string, lotUuid: string) => void} props.openMoveInventoryModal
 * @param {string} props.inventoryLocationId
 * @param {() => void} props.onReloadLocations
 * @param {boolean} props.productActive
 * @returns {JSX.Element}
 */
export const InventoryLocation = ({
  productId,
  warehouseName = '',
  location = '',
  lot = '',
  lotUuid = '',
  expirationDate = '',
  stock = 0,
  allocated = 0,
  available = 0,
  openMoveInventoryModal,
  inventoryLocationId,
  onReloadLocations,
  productActive,
}) => {
  const isLotTrackingEnabled = useEnableLotTracking();
  const onOpenMoveModal = useCallback(() => {
    openMoveInventoryModal(inventoryLocationId, lotUuid);
  }, [openMoveInventoryModal, inventoryLocationId, lotUuid]);
  const formattedExpirationDate = useUtcFormattedDate(expirationDate);

  const { open: openHistory, close: closeHistory, isOpen: isOpenHistory } = useModal(false);
  const { open: openAddInventory, close: closeAddInventory, isOpen: isOpenAddInventory } = useModal(false);
  const { open: openRemoveInventory, close: closeRemoveInventory, isOpen: isOpenRemoveInventory } = useModal(false);
  const { open: openSetInventory, close: closeSetInventory, isOpen: isOpenSetInventory } = useModal(false);
  const { open: openEditLot, close: closeEditLot, isOpen: isOpenEditLot } = useModal(false);

  const actionsCell = (
    <div className={style.centered} data-testid='inventory-adjustment-actions'>
      <LocationActionsMenu>
        <DropdownMenuItem onClick={openAddInventory}>
          <Text textType={TextType.Paragraph} value={messages.addInventoryActionMenu} />
        </DropdownMenuItem>
        <DropdownMenuItem onClick={openRemoveInventory}>
          <Text textType={TextType.Paragraph} value={messages.removeInventoryActionMenu} />
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onOpenMoveModal}>
          <Text textType={TextType.Paragraph} value={messages.moveInventoryActionMenu} />
        </DropdownMenuItem>
        <DropdownMenuItem onClick={openSetInventory}>
          <Text textType={TextType.Paragraph} value={messages.setInventoryActionMenu} />
        </DropdownMenuItem>
        <DropdownMenuItem onClick={openHistory}>
          <Text textType={TextType.Paragraph} value={messages.historyActionMenu} />
        </DropdownMenuItem>
        {isLotTrackingEnabled && lotUuid ? (
          <DropdownMenuItem onClick={openEditLot}>
            <Text textType={TextType.Paragraph} value={messages.editLotActionMenu} />
          </DropdownMenuItem>
        ) : null}
      </LocationActionsMenu>
    </div>
  );

  const renderActionsCell = () => {
    if (isOpenAddInventory) {
      return (
        <AddInventoryPopover
          inventoryLocationId={inventoryLocationId}
          isPopoverOpen
          lotUuid={lotUuid}
          onClose={closeAddInventory}
          onReloadLocations={onReloadLocations}
          productId={productId}
          target={actionsCell}
        />
      );
    }
    if (isOpenRemoveInventory) {
      return (
        <SubtractInventoryPopover
          inventoryLocationId={inventoryLocationId}
          isPopoverOpen
          lotUuid={lotUuid}
          onClose={closeRemoveInventory}
          productId={productId}
          target={actionsCell}
        />
      );
    }
    if (isOpenSetInventory) {
      return (
        <SetInventoryPopover
          inventoryLocationId={inventoryLocationId}
          isPopoverOpen
          lotUuid={lotUuid}
          onClose={closeSetInventory}
          onReloadLocations={onReloadLocations}
          productId={productId}
          stock={stock}
          target={actionsCell}
        />
      );
    }
    if (isOpenEditLot) {
      return (
        <EditLotPopover
          expirationDate={expirationDate}
          isPopoverOpen
          lot={lot}
          lotUuid={lotUuid}
          onClose={closeEditLot}
          onReloadLocations={onReloadLocations}
          productId={productId}
          target={actionsCell}
        />
      );
    }
    return actionsCell;
  };

  return (
    <div
      className={classnames({
        [style['locations-item-extended']]: isLotTrackingEnabled,
        [style['locations-item']]: !isLotTrackingEnabled,
      })}
    >
      <div>{warehouseName}</div>
      <div>{location}</div>
      {isLotTrackingEnabled ? (
        <>
          <div>
            <div className={style['text-overflow-wrap']}>{lot}</div>
          </div>
          <div>{formattedExpirationDate}</div>
        </>
      ) : null}
      <div className={style.centered}>{stock}</div>
      <div className={style.centered}>{allocated}</div>
      <div className={style.centered}>{available}</div>
      {productActive ? renderActionsCell() : null}
      <div className={style.centered}>
        {isOpenHistory ? (
          <InventoryHistoryModal
            expirationDate={expirationDate}
            inventoryLocationId={inventoryLocationId}
            lot={lot}
            lotUuid={lotUuid}
            onCloseModal={closeHistory}
          />
        ) : null}
      </div>
    </div>
  );
};
